import { EnglishFlagIcon, SpanishFlagIcon } from "@worship-devo/ui";

export const DEFAULT_TZ = "America/Chicago";

const UTM_PARAMS_DEFAULT = [
  "ref_user",
  "share_id",
  "share_source",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

export const UTM_PARAMS: ReadonlyArray<string> =
  typeof import.meta.env.VITE_UTM_PARAMS === "string"
    ? Array.from(new Set([...UTM_PARAMS_DEFAULT, ...(import.meta.env.VITE_UTM_PARAMS as string).split(",")]))
    : UTM_PARAMS_DEFAULT;

export const PROGRESS_UPDATE_INTERVAL: number =
  typeof import.meta.env.VITE_PROGRESS_UPDATE_INTERVAL === "string"
    ? parseInt(import.meta.env.VITE_PROGRESS_UPDATE_INTERVAL, 10)
    : 30;

export const EPISODE_COMPLETE_THRESHOLD = 0.75;
export const EPISODE_COMPLETE_THRESHOLD_PERCENTAGE = EPISODE_COMPLETE_THRESHOLD * 100;
export const EPISODE_COMPLETION_DRAWER_THRESHOLD_PERCENTAGE = 95;
export const STREAK_TOAST_THRESHOLD = 0.9;
export const STREAK_TOAST_THRESHOLD_PERCENTAGE = STREAK_TOAST_THRESHOLD * 100;

export const DEFAULT_NOTIFICATION_TIME = "07:00:00";

export const DESKTOP_BREAKPOINT_QUERY = "(min-width: 1024px)";

export const COMMON_TIMEZONES_LIST = [
  // US Timezones (East to West)
  { value: "America/New_York", label: "New York" },
  { value: "America/Chicago", label: "Chicago" },
  { value: "America/Denver", label: "Denver" },
  { value: "America/Los_Angeles", label: "Los Angeles" },
  { value: "America/Anchorage", label: "Anchorage" },
  { value: "America/Juneau", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Honolulu" },

  // Other timezones by UTC offset (West to East)
  { value: "Pacific/Midway", label: "Midway Island, Samoa" },
  { value: "America/Nuuk", label: "Greenland" },
  { value: "America/St_Johns", label: "Newfoundland and Labrador" },
  { value: "America/Sao_Paulo", label: "Brasilia" },
  { value: "America/Montevideo", label: "Montevideo" },
  { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires, Georgetown" },
  { value: "America/Caracas", label: "Caracas, La Paz" },
  { value: "America/Santiago", label: "Santiago" },
  { value: "America/Bogota", label: "Bogota, Lima, Quito" },
  { value: "America/Regina", label: "Saskatchewan" },
  { value: "America/Mexico_City", label: "Guadalajara, Mexico City, Monterrey" },
  { value: "America/Belize", label: "Central America" },
  { value: "America/Chihuahua", label: "Chihuahua, La Paz, Mazatlan" },
  { value: "America/Tijuana", label: "Tijuana" },
  { value: "America/Dawson", label: "Dawson, Yukon" },
  { value: "Atlantic/Azores", label: "Azores" },
  { value: "Atlantic/Cape_Verde", label: "Cape Verde Islands" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Dublin", label: "Dublin" },
  { value: "Europe/Lisbon", label: "Lisbon" },
  { value: "Africa/Casablanca", label: "Casablanca, Monrovia" },
  { value: "Atlantic/Canary", label: "Canary Islands" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Belgrade", label: "Belgrade, Bratislava, Budapest, Ljubljana, Prague" },
  { value: "Europe/Sarajevo", label: "Sarajevo, Skopje, Warsaw, Zagreb" },
  { value: "Europe/Brussels", label: "Brussels, Copenhagen, Madrid, Paris" },
  { value: "Europe/Amsterdam", label: "Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna" },
  { value: "Africa/Algiers", label: "West Central Africa" },
  { value: "Europe/Bucharest", label: "Bucharest" },
  { value: "Africa/Cairo", label: "Cairo" },
  { value: "Europe/Helsinki", label: "Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius" },
  { value: "Europe/Athens", label: "Athens" },
  { value: "Asia/Jerusalem", label: "Jerusalem" },
  { value: "Africa/Harare", label: "Harare, Pretoria" },
  { value: "Europe/Moscow", label: "Istanbul, Minsk, Moscow, St. Petersburg, Volgograd" },
  { value: "Asia/Kuwait", label: "Kuwait, Riyadh" },
  { value: "Africa/Nairobi", label: "Nairobi" },
  { value: "Asia/Baghdad", label: "Baghdad" },
  { value: "Asia/Tehran", label: "Tehran" },
  { value: "Asia/Dubai", label: "Abu Dhabi, Muscat" },
  { value: "Asia/Baku", label: "Baku, Tbilisi, Yerevan" },
  { value: "Asia/Kabul", label: "Kabul" },
  { value: "Asia/Yekaterinburg", label: "Ekaterinburg" },
  { value: "Asia/Karachi", label: "Islamabad, Karachi, Tashkent" },
  { value: "Asia/Kolkata", label: "Chennai, Kolkata, Mumbai, New Delhi" },
  { value: "Asia/Colombo", label: "Sri Jayawardenepura" },
  { value: "Asia/Kathmandu", label: "Kathmandu" },
  { value: "Asia/Dhaka", label: "Astana, Dhaka" },
  { value: "Asia/Almaty", label: "Almaty, Novosibirsk" },
  { value: "Asia/Yangon", label: "Yangon (Rangoon)" },
  { value: "Asia/Bangkok", label: "Bangkok, Hanoi, Jakarta" },
  { value: "Asia/Krasnoyarsk", label: "Krasnoyarsk" },
  { value: "Asia/Shanghai", label: "Beijing, Chongqing, Hong Kong SAR, Urumqi" },
  { value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur, Singapore" },
  { value: "Asia/Taipei", label: "Taipei" },
  { value: "Australia/Perth", label: "Perth" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Irkutsk", label: "Irkutsk, Ulaanbaatar" },
  { value: "Asia/Seoul", label: "Seoul" },
  { value: "Asia/Yakutsk", label: "Yakutsk" },
  { value: "Australia/Darwin", label: "Darwin" },
  { value: "Australia/Adelaide", label: "Adelaide" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Australia/Brisbane", label: "Brisbane" },
  { value: "Australia/Hobart", label: "Hobart" },
  { value: "Asia/Vladivostok", label: "Vladivostok" },
  { value: "Pacific/Guam", label: "Guam, Port Moresby" },
  { value: "Asia/Magadan", label: "Magadan, Solomon Islands, New Caledonia" },
  { value: "Asia/Kamchatka", label: "Kamchatka, Marshall Islands" },
  { value: "Pacific/Fiji", label: "Fiji Islands" },
  { value: "Pacific/Auckland", label: "Auckland, Wellington" },
  { value: "Pacific/Tongatapu", label: "Nuku'alofa" },
] as const;

export const NEW_EPISODES_LOOKBACK_MS: number =
  (typeof import.meta.env.VITE_NEW_EPISODES_LOOKBACK_HOURS === "string"
    ? Number(import.meta.env.VITE_NEW_EPISODES_LOOKBACK_HOURS)
    : 36) *
  60 *
  60 *
  1000;

export const STORAGE_KEYS = {
  UPGRADE_SUCCESS: "upgrade_success",
  NOTIFICATION_CUSTOMIZATION_EXPIRATION: "popup_seen_notification_customization_expiration",
  SERIES_SORT_ORDER: "series-sort-order",
  SHOW_NOTIFICATION_CUSTOMIZATION_POPUP: "show_notification_customization_popup",
  HOME_WELCOME_BANNER_DISMISSED: "home_welcome_banner_dismissed",
  SING_PLUS_PAYMENT_METHOD_WARNING_DISMISSED: "sing_plus_payment_method_warning_dismissed",
  COUPON_CODE: "couponCode",
  GIFT_CHECKOUT_SESSION_ID: "gift-checkout-session-id",
  GIFT_CHECKOUT_STARTED_AT: "gift-checkout-started-at",
  POST_SIGNUP_SMS_OPT_IN: "post-signup-sms-opt-in",
  FIRST_WEEK_COMPLETION_ACTIVATION: "first-week-completion-activation",
} as const;

export const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;

export type CommonTimezoneId = (typeof COMMON_TIMEZONES_LIST)[number]["value"];

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English", FlagIcon: EnglishFlagIcon },
  { value: "es", label: "Spanish", FlagIcon: SpanishFlagIcon },
] as const;

export type LanguageOptionValue = (typeof LANGUAGE_OPTIONS)[number]["value"];

export const MONTHS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Authentication-related URLs
export const AUTH_PRIVACY_POLICY_URL = "https://getpsallo.theworshipinitiative.com/legal/privacy-policy";
export const AUTH_TERMS_URL = "https://www.theworshipinitiative.com/terms-of-service";
// biome-ignore lint/security/noSecrets: existing product support mailto destination
export const AUTH_SUPPORT_EMAIL_URL = "mailto:help@worshipinitiative.com?subject=Sing%20Support%20Request";
export const INCOGNITO_EDUCATION_FAQ_URL =
  "https://knowledge.worshipinitiative.com/why-do-i-have-to-receive-a-sign-in-code-each-day-for-the-daily-devo";
